<template>
    <zen-slide-in :isOpen="isOpen" title="Swap history" @close="$emit('close')">
        <template #body>
            <p class="text-sm text-taupe-600 mb-6">
                Every swap applied to this environment: who created it, what it
                changed, and when it ended.
            </p>

            <div class="space-y-5">
                <div>
                    <label
                        for="swap-history-environment"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Environment
                    </label>
                    <div class="mt-2">
                        <zen-select
                            id="swap-history-environment"
                            :model-value="targetKey"
                            :options="environments"
                            option-label="name"
                            option-value="key"
                            data-testid="swap-history-environment"
                            @update:model-value="
                                $emit('environment-change', $event)
                            "
                        />
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                        <label
                            for="swap-history-actor"
                            class="block text-sm font-medium text-taupe-700 mb-1"
                        >
                            Actor
                        </label>
                        <select
                            id="swap-history-actor"
                            :value="filters.actorUlid"
                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            data-testid="swap-history-actor"
                            @change="
                                updateFilter('actorUlid', $event.target.value)
                            "
                        >
                            <option value="">All actors</option>
                            <option
                                v-for="actor in actors"
                                :key="actor.ulid"
                                :value="actor.ulid"
                            >
                                {{ actor.name }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label
                            for="swap-history-from"
                            class="block text-sm font-medium text-taupe-700 mb-1"
                        >
                            From
                        </label>
                        <input
                            id="swap-history-from"
                            type="date"
                            :value="filters.createdFrom"
                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            data-testid="swap-history-from"
                            @change="
                                updateFilter('createdFrom', $event.target.value)
                            "
                        />
                    </div>

                    <div>
                        <label
                            for="swap-history-to"
                            class="block text-sm font-medium text-taupe-700 mb-1"
                        >
                            To
                        </label>
                        <input
                            id="swap-history-to"
                            type="date"
                            :value="filters.createdTo"
                            class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            data-testid="swap-history-to"
                            @change="
                                updateFilter('createdTo', $event.target.value)
                            "
                        />
                    </div>
                </div>

                <zen-loading-indicator v-if="loading" />

                <p
                    v-else-if="events.length === 0"
                    class="text-sm text-taupe-500"
                    data-testid="swap-history-empty"
                >
                    No swaps match these filters.
                </p>

                <ol
                    v-else
                    class="divide-y divide-taupe-200 rounded-md border border-taupe-200 bg-white px-4"
                    data-testid="swap-history-list"
                >
                    <li
                        v-for="event in events"
                        :key="event.ulid"
                        class="py-3 space-y-1"
                        data-testid="swap-history-event"
                    >
                        <p class="text-sm font-medium text-taupe-900">
                            {{ actionLabel(event) }}
                            <span class="font-normal text-taupe-500">
                                {{ byline(event) }}
                            </span>
                        </p>
                        <p class="text-xs text-taupe-500">
                            {{ environmentName(event.source_environment_ulid) }}
                            into
                            {{ environmentName(event.target_environment_ulid) }}
                            ·
                            <zen-date-time :value="event.created_at" />
                        </p>
                        <p
                            v-if="event.snapshot"
                            class="text-xs text-taupe-700"
                            data-testid="swap-history-snapshot"
                        >
                            {{ snapshotSummary(event.snapshot) }}
                        </p>
                        <p
                            v-else-if="event.flags?.length"
                            class="font-mono text-xs text-taupe-700"
                            data-testid="swap-history-flags"
                        >
                            {{ flagNames(event) }}
                        </p>
                        <p
                            v-if="event.action === 'flag_swap.created'"
                            class="text-xs text-taupe-500"
                        >
                            Ends
                            <zen-date-time :value="event.expires_at" />
                        </p>
                    </li>
                </ol>

                <div v-if="hasMore" class="flex justify-center">
                    <zen-button
                        variant="secondary"
                        :processing="loadingMore"
                        data-testid="swap-history-load-more"
                        @click="$emit('load-more')"
                    >
                        Load more
                    </zen-button>
                </div>
            </div>
        </template>
        <template #buttons>
            <zen-button @click="$emit('close')" variant="secondary">
                Close
            </zen-button>
        </template>
    </zen-slide-in>
</template>

<script setup>
/**
 * Audit history of the temporary swaps applied to one environment (ZEN-767).
 * It holds no data of its own: the screen passes in the page of events, the
 * filter values and the environment, and this component reports changes back.
 */
const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    // Environments on the screen, used for the environment picker and to name each swap's source and target.
    environments: {
        type: Array,
        default: () => [],
    },
    // Key of the environment whose history is shown.
    targetKey: {
        type: String,
        default: null,
    },
    // History rows, newest first, from the history endpoint.
    events: {
        type: Array,
        default: () => [],
    },
    // Users who appear as actors in this environment's history, for the actor filter.
    actors: {
        type: Array,
        default: () => [],
    },
    // Current filter values: actorUlid, createdFrom and createdTo (dates as YYYY-MM-DD, '' when unset).
    filters: {
        type: Object,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    loadingMore: {
        type: Boolean,
        default: false,
    },
    hasMore: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits([
    'close',
    'environment-change',
    'filter-change',
    'load-more',
])

const ACTION_LABELS = {
    'flag_swap.created': 'Swap created',
    'flag_swap.reverted': 'Swap reverted',
    'flag_swap.expired': 'Swap expired',
}

const actionLabel = (event) => ACTION_LABELS[event.action] ?? 'Swap event'

// A manual revert names the user who did it. Scheduled expiry has no actor.
const byline = (event) =>
    event.actor ? `by ${event.actor.name}` : 'automatically'

const environmentName = (ulid) =>
    props.environments.find((environment) => environment.ulid === ulid)?.name ??
    'Unknown environment'

const flagNames = (event) =>
    event.flags.map((flag) => flag.flag_name).join(', ')

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

// A whole-environment swap copied every flag, so the history gives the totals instead of listing them.
const snapshotSummary = (snapshot) =>
    `Whole environment: ${plural(snapshot.flag_count, 'flag')}, ${plural(
        snapshot.rule_count,
        'rule'
    )}, ${plural(snapshot.rollout_count, 'rollout')}`

const updateFilter = (key, value) => {
    emit('filter-change', { ...props.filters, [key]: value })
}
</script>
